import React, { useContext, useEffect, useState } from 'react';
import {
  IProduct,
  useCreateMembershipMutation,
  useMembershipProductsQuery
} from '../../generated/graphql';
import { sameMembershipNameExist } from '../../utils/clientUtils';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import DialogBox from '../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import InputComponent from '../Common/Fields/InputComponent';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';

type IProps = {
  handleClose: () => void;
};

export type IErrors = {
  name: string | null;
  type: string | null;
  discount: string | null;
  startDate: string | null;
  renewalDate: string | null;
  endDate: string | null;
};

export const ErrorTexts = {
  name: 'Name is required',
  nameExists: 'Name already exists',
  type: 'Type is required',
  discount: 'Discount should be bigger than 0',
  startDate: 'Start date is required',
  startDateInvalidDate: 'Invalid Date Format',
  renewalDateBeforeMin: 'Renewal date should not be before start date',
  endDateBeforeMin: 'End date should not be before start date'
};

const defaultErrorsObj: IErrors = {
  name: null,
  type: null,
  discount: null,
  startDate: null,
  renewalDate: null,
  endDate: null
};

const minAllowedDate = new Date(2000, 0, 1);

const NewMembership: React.FunctionComponent<IProps> = ({ handleClose }: IProps) => {
  const membershipProducts = useMembershipProductsQuery();
  const [name, setName] = useState('');
  const [membershipType, setMembershipType] = useState<IProduct | null>(null);
  const [discount, setDiscount] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [renewalDate, setRenewalDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createMembership, createMembershipResult] = useCreateMembershipMutation();
  const { dispatch } = useContext(AppContext);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        name: ErrorTexts.name
      });
    } else {
      setFormErrors({ ...formErrors, name: null });
    }
    setName(value);
  };

  const handleMembershipTypeChange = (_: unknown, newValue: IProduct | null) => {
    setDiscount('');
    if (newValue) {
      const product = newValue as IProduct;
      ErrorTexts.discount = `Discount should be between 0 and ${product.price}`;
      setFormErrors({ ...formErrors, type: null, discount: null });
    } else {
      ErrorTexts.discount = `Discount should be bigger than 0`;
      setFormErrors({ ...formErrors, type: ErrorTexts.type, discount: null });
    }

    setMembershipType(newValue);
  };

  const handleDiscountChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    const productPrice = membershipType?.price as number;
    if (value && (Number(value) < 0 || Number(value) > productPrice)) {
      setFormErrors({
        ...formErrors,
        discount: ErrorTexts.discount
      });
    } else {
      setFormErrors({ ...formErrors, discount: null });
    }
    setDiscount(value);
  };

  const getTotalCost = () => {
    return membershipType ? (membershipType.price - Number(discount)).toString() : '';
  };

  const handleStartDateChange = (value: Date | null) => {
    if (value) {
      let errorDto = { ...formErrors, startDate: null };
      if (renewalDate) {
        if (value >= renewalDate) {
          errorDto = { ...errorDto, renewalDate: ErrorTexts.renewalDateBeforeMin };
        }
      }
      if (endDate) {
        if (value >= endDate) {
          errorDto = { ...errorDto, endDate: ErrorTexts.endDateBeforeMin };
        }
      }

      setFormErrors(errorDto);
    } else {
      setFormErrors({ ...formErrors, startDate: ErrorTexts.startDate });
    }

    setStartDate(value);
  };

  const handleRenewalDateChange = (value: Date | null) => {
    let errorDbo = { ...formErrors };
    if (value) {
      if (startDate && value >= startDate) {
        errorDbo = { ...errorDbo, renewalDate: null };
      }
      if (endDate && value > endDate) {
        const newEndDateErrorMsg = `End date should not be before renewal date`;
        errorDbo = { ...errorDbo, endDate: newEndDateErrorMsg };
      }
    } else {
      errorDbo = { ...errorDbo, renewalDate: null };
    }
    setFormErrors(errorDbo);
    setRenewalDate(value);
  };

  const handleEndDateChange = (value: Date | null) => {
    if (value) {
      if (renewalDate) {
        if (value >= renewalDate) {
          setFormErrors({ ...formErrors, endDate: null });
        }
      } else {
        if (startDate) {
          if (value >= startDate) {
            setFormErrors({ ...formErrors, endDate: null });
          }
        } else {
          setFormErrors({ ...formErrors, endDate: null });
        }
      }
    }
    setEndDate(value);
  };

  const submitForm = () => {
    const errors = {
      name: null,
      type: null,
      discount: null,
      startDate: null
    } as IErrors;

    let hasErrors = false;
    if (!name || name.length < 2) {
      hasErrors = true;
      errors.name = ErrorTexts.name;
    }
    if (!membershipType) {
      hasErrors = true;
      errors.type = ErrorTexts.type;
    }
    const productPrice = membershipType?.price as number;
    if (discount && (Number(discount) < 0 || Number(discount) > productPrice)) {
      hasErrors = true;
      errors.discount = ErrorTexts.discount;
    }
    if (!startDate) {
      hasErrors = true;
      errors.startDate = ErrorTexts.startDate;
    } else {
      if (!Date.parse(startDate.toString())) {
        hasErrors = true;
        errors.startDate = ErrorTexts.startDateInvalidDate;
      }
    }

    if (renewalDate) {
      if (Date.parse(renewalDate.toString())) {
        if (startDate) {
          if (startDate > renewalDate) {
            hasErrors = true;
          }
        }
      } else {
        hasErrors = true;
      }
    }
    if (endDate) {
      if (Date.parse(endDate.toString())) {
        if (renewalDate) {
          if (renewalDate > endDate) {
            hasErrors = true;
          }
        } else {
          if (startDate) {
            if (startDate > endDate) {
              hasErrors = true;
            }
          }
        }
      } else {
        hasErrors = true;
      }
    }
    if (!hasErrors && sameMembershipNameExist(name)) {
      hasErrors = true;
      errors.name = ErrorTexts.nameExists;
    }

    if (hasErrors) {
      setFormErrors(errors);
    } else {
      submitMembership();
    }
  };

  const submitMembership = () => {
    dispatch({ type: Actions.HideConfirmation });
    const input = {
      name: name.trim(),
      product_id: parseInt(membershipType?.id as string),
      start_date: startDate ? startDate.toString() : '',
      end_date: endDate ? endDate.toString() : '',
      renewable_date: renewalDate ? renewalDate.toString() : '',
      discount: discount ? parseFloat(discount) : null,
      total: parseFloat(getTotalCost())
    };
    createMembership({
      variables: {
        input
      },
      update(_, { data }) {
        if (data?.result) {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Membership ${data.result.name} created`,
              severity: 'success',
              autoHide: 2000
            }
          });
          handleClose();
        }
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message,
          autoHide: 6000
        }
      });
    });
  };

  return (
    <DialogBox id="new-membership" width={880} title="Create Membership" onClose={handleClose}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '40px 22px',
          padding: '50px 210px'
        }}
      >
        <InputComponent
          id="new-membership-name"
          label="Name"
          value={name}
          onChange={handleNameChange}
          errorMsg={formErrors.name}
          disabled={createMembershipResult.loading}
          additionalStyle={{ gridColumn: 'span 3', marginTop: '50px' }}
        />
        <AutocompleteComponent
          id="new-membership-type"
          label="Type"
          options={(membershipProducts.data?.result as never[]) || []}
          disabled={createMembershipResult.loading}
          values={membershipType}
          onChange={handleMembershipTypeChange}
          getOptionLabel={(option: IProduct) => option?.membership_type?.name as string}
          errorMsg={formErrors.type}
          additionalStyle={{ gridColumn: 'span 3' }}
        />
        <InputComponent
          id="new-membership-price"
          label="Price"
          value={membershipType ? membershipType.price : ''}
          disabled={true}
        />
        <InputComponent
          id="new-membership-discount"
          type="number"
          label="Discount"
          value={discount}
          onChange={handleDiscountChange}
          errorMsg={formErrors.discount}
          disabled={createMembershipResult.loading}
        />
        <InputComponent
          id="new-membership-total-cost"
          label="Total Cost"
          value={getTotalCost()}
          disabled={true}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <EpraDatePicker
            id="new-membership-renewal-date"
            label="Start"
            value={startDate}
            onChange={handleStartDateChange}
            errorMsg={formErrors.startDate}
            minDate={minAllowedDate}
            disabled={createMembershipResult.loading}
          />
          <EpraDatePicker
            id="new-membership-start-date"
            label="Renewal"
            value={renewalDate}
            onChange={handleRenewalDateChange}
            minDate={startDate ?? undefined}
            errorMsg={formErrors.renewalDate ?? undefined}
            disabled={createMembershipResult.loading}
          />
          <EpraDatePicker
            id="new-membership-end-date"
            label="End"
            value={endDate}
            onChange={handleEndDateChange}
            minDate={
              (renewalDate
                ? Date.parse(renewalDate.toString())
                  ? renewalDate
                  : startDate
                : startDate) ?? undefined
            }
            errorMsg={formErrors.endDate ?? undefined}
            disabled={createMembershipResult.loading}
          />
        </LocalizationProvider>
        <Box className="centralize" sx={{ gridColumn: 'span 3', marginTop: '100px' }}>
          <CancelButton
            id="cancel-new-membership-button"
            disabled={createMembershipResult.loading}
            onClick={handleClose}
            text="Cancel"
          />
          <SaveButton
            id="create-new-membership-button"
            disabled={createMembershipResult.loading}
            onClick={submitForm}
            text="Create"
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default NewMembership;
